import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
    Box,
    Card,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Stack,
    Typography,
    Breadcrumbs,
    Link as MuiLink,
    Tabs,
    Tab,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined'
import { useGetReleaseNotesQuery } from '../../redux/commonApiCalls'
import PageTitle from '../../components/PageTitle'

const TYPE_COLOR = {
    feature: { bg: '#eef2ff', fg: '#4f46e5', dot: '#4f46e5' },
    fix: { bg: '#fef3c7', fg: '#b45309', dot: '#f59e0b' },
    improvement: { bg: '#dcfce7', fg: '#15803d', dot: '#22c55e' },
    breaking: { bg: '#fee2e2', fg: '#b91c1c', dot: '#ef4444' },
    update: { bg: '#e0e7ff', fg: '#4338ca', dot: '#6366f1' },
}

const typeStyle = (t) => TYPE_COLOR[(t || '').toLowerCase()] || TYPE_COLOR.update

const formatDate = (val) => {
    if (!val) return '—'
    const d = new Date(val)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '')

/* ─────────────── lightweight markdown renderer ─────────────── */
const renderInline = (text) => {
    if (!text) return null
    const parts = []
    let remaining = String(text)
    let key = 0
    const boldRegex = /\*\*([^*]+)\*\*/
    while (remaining.length) {
        const m = remaining.match(boldRegex)
        if (!m) {
            parts.push(<span key={key++}>{remaining}</span>)
            break
        }
        const before = remaining.slice(0, m.index)
        if (before) parts.push(<span key={key++}>{before}</span>)
        parts.push(<strong key={key++}>{m[1]}</strong>)
        remaining = remaining.slice(m.index + m[0].length)
    }
    return parts
}

const renderMarkdown = (content) => {
    if (!content) return null
    const lines = String(content).split('\n')
    const blocks = []
    let key = 0
    let listBuffer = []

    const flushList = () => {
        if (listBuffer.length) {
            blocks.push(
                <Box component='ul' key={`ul-${key++}`} sx={{ pl: 3, my: 1, '& li': { mb: 0.5 } }}>
                    {listBuffer.map((item, i) => (
                        <li key={i}>
                            <Typography variant='body2' color='text.primary' sx={{ lineHeight: 1.7 }}>
                                {renderInline(item)}
                            </Typography>
                        </li>
                    ))}
                </Box>
            )
            listBuffer = []
        }
    }

    lines.forEach((rawLine) => {
        const line = rawLine.trim()
        const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/)
        if (imgMatch) {
            flushList()
            blocks.push(
                <Box key={`img-${key++}`} sx={{ my: 2 }}>
                    <img
                        src={imgMatch[2]}
                        alt={imgMatch[1] || 'release'}
                        style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                </Box>
            )
            return
        }
        if (line.startsWith('### ')) {
            flushList()
            blocks.push(<Typography key={`h3-${key++}`} variant='subtitle1' fontWeight={700} sx={{ mt: 2, mb: 1 }}>{renderInline(line.slice(4))}</Typography>)
            return
        }
        if (line.startsWith('## ')) {
            flushList()
            blocks.push(<Typography key={`h2-${key++}`} variant='h6' fontWeight={700} sx={{ mt: 2, mb: 1 }}>{renderInline(line.slice(3))}</Typography>)
            return
        }
        if (line.startsWith('# ')) {
            flushList()
            blocks.push(<Typography key={`h1-${key++}`} variant='h5' fontWeight={700} sx={{ mt: 2, mb: 1 }}>{renderInline(line.slice(2))}</Typography>)
            return
        }
        if (line.startsWith('- ') || line.startsWith('* ')) {
            listBuffer.push(line.slice(2))
            return
        }
        if (!line) {
            flushList()
            blocks.push(<Box key={`br-${key++}`} sx={{ height: 8 }} />)
            return
        }
        flushList()
        blocks.push(
            <Typography key={`p-${key++}`} variant='body2' color='text.primary' sx={{ mb: 1.25, lineHeight: 1.75 }}>
                {renderInline(line)}
            </Typography>
        )
    })
    flushList()
    return blocks
}

/* ─────────────── helpers ─────────────── */
const getNoteType = (note) =>
    note?.changeType || note?.type || note?.changes?.[0]?.type || ''

/* ─────────────── component ─────────────── */
export default function ReleaseNoteDetail() {
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams()

    const stateNote = location.state?.note || null
    const [tab, setTab] = useState(0)

    // Only fetch the list if we don't already have the note in route state.
    // RTK Query dedupes — if list/popover already loaded the data, this is a cache hit.
    const { data, isFetching, error: queryError } = useGetReleaseNotesQuery(
        { limit: 100 },
        { skip: !!stateNote }
    )

    const note = useMemo(() => {
        if (stateNote) return stateNote
        const list = data?.items || []
        return list.find((n) => String(n._id || n.id || n.shortCode) === String(id)) || null
    }, [stateNote, data, id])

    const loading = !stateNote && isFetching && !note
    const error = !note && !isFetching
        ? (queryError ? (queryError?.data?.message || 'Failed to load release notes') : (stateNote ? null : 'Release note not found'))
        : null

    const changes = useMemo(() => {
        if (!note) return []
        if (Array.isArray(note.changes) && note.changes.length) return note.changes
        const fallback = note.description || note.content || note.body
        return fallback ? [{ type: getNoteType(note) || 'update', description: fallback }] : []
    }, [note])

    const changeTypes = useMemo(() => {
        const map = new Map()
        changes.forEach((c) => {
            const t = (c.type || c.changeType || 'update').toString().toLowerCase()
            map.set(t, (map.get(t) || 0) + 1)
        })
        return Array.from(map.entries())
    }, [changes])

    const filteredChanges = useMemo(() => {
        if (changeTypes.length <= 1) return changes
        if (tab === 0) return changes
        const t = changeTypes[tab - 1]?.[0]
        if (!t) return changes
        return changes.filter((c) => (c.type || c.changeType || 'update').toLowerCase() === t)
    }, [changes, changeTypes, tab])

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
    }
    if (error || !note) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color='error'>{error || 'Note not found'}</Typography>
                <MuiLink onClick={() => navigate('/whats-new')} sx={{ cursor: 'pointer', mt: 2, display: 'inline-block' }}>
                    Back to What&apos;s New
                </MuiLink>
            </Box>
        )
    }

    const noteId = note._id || note.id || note.shortCode
    const noteType = getNoteType(note)
    const noteTypeStyle = typeStyle(noteType)
    const releaseDate = note.releaseDate || note.publishedAt || note.createdAt
    const publishedAt = note.publishedAt || note.createdAt
    const publishedBy = note.publishedBy || note.createdBy || note.author
    const createdBy = note.createdBy || note.author
    const modifiedBy = note.modifiedBy || note.updatedBy || publishedBy
    const lastUpdated = note.updatedAt || note.lastUpdated || note.modifiedAt || publishedAt
    const status = note.status || 'Published'
    const audienceList = Array.isArray(note.audience) ? note.audience : (note.audience ? [note.audience] : [])

    return (
        <>
            <PageTitle>Release Note</PageTitle>

            {/* ───── breadcrumb row ───── */}
            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
                <IconButton
                    onClick={() => navigate('/whats-new')}
                    size='small'
                    sx={{
                        border: '1px solid',
                        borderColor: 'grey.300',
                        backgroundColor: '#fff',
                        '&:hover': { backgroundColor: '#f3f4f6' },
                    }}
                >
                    <ArrowBackIcon fontSize='small' />
                </IconButton>
                <Breadcrumbs separator='/' sx={{ '& a': { cursor: 'pointer' } }}>
                    <MuiLink underline='hover' color='text.secondary' onClick={() => navigate('/whats-new')}>
                        What&apos;s New
                    </MuiLink>
                    <Typography color='text.primary' fontWeight={600} sx={{
                        maxWidth: 480,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {note.title || note.headline || 'Release'}
                    </Typography>
                </Breadcrumbs>
            </Stack>

            {/* ───── header card ───── */}
            <Card
                elevation={0}
                sx={{
                    borderRadius: 2,
                    p: 3,
                    mb: 2.5,
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    backgroundColor: '#fff',
                }}
            >
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                    background: 'linear-gradient(90deg, #5b67d8 0%, #06b6d4 100%)',
                }} />

                {/* identity chip row */}
                <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                    {noteId && (
                        <Chip
                            label={noteId}
                            size='small'
                            variant='outlined'
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: 11,
                                height: 24,
                                borderColor: 'grey.300',
                                color: 'text.secondary',
                            }}
                        />
                    )}
                    <Chip
                        label={titleCase(status)}
                        size='small'
                        sx={{
                            backgroundColor: '#dcfce7',
                            color: '#15803d',
                            fontWeight: 700,
                            fontSize: 11,
                            height: 24,
                        }}
                    />
                    {noteType && (
                        <Chip
                            label={
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: noteTypeStyle.dot }} />
                                    {titleCase(noteType)}
                                </span>
                            }
                            size='small'
                            sx={{
                                backgroundColor: noteTypeStyle.bg,
                                color: noteTypeStyle.fg,
                                fontWeight: 700,
                                fontSize: 11,
                                height: 24,
                                '& .MuiChip-label': { px: 1.25 },
                            }}
                        />
                    )}
                    {note.highlight && (
                        <Chip
                            label='Highlight'
                            size='small'
                            sx={{
                                backgroundColor: '#fef3c7',
                                color: '#b45309',
                                fontWeight: 700,
                                fontSize: 11,
                                height: 24,
                            }}
                        />
                    )}
                    {Array.isArray(note.tags) && note.tags.map((t) => (
                        <Chip key={t} label={t} size='small' variant='outlined' sx={{ fontSize: 11, height: 24 }} />
                    ))}
                </Stack>

                <Typography variant='h4' fontWeight={700} color='text.primary' sx={{ mb: 1, lineHeight: 1.2 }}>
                    {note.title || note.headline}
                </Typography>
                {(note.summary || note.description) && (
                    <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
                        {note.summary || note.description}
                    </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Stack direction='row' spacing={3} flexWrap='wrap' sx={{ rowGap: 1.25 }}>
                    <Stack direction='row' spacing={0.75} alignItems='center'>
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant='caption' color='text.secondary'>
                            Released&nbsp;<Box component='span' sx={{ color: 'text.primary', fontWeight: 700 }}>{formatDate(releaseDate)}</Box>
                        </Typography>
                    </Stack>
                    {publishedBy && (
                        <Stack direction='row' spacing={0.75} alignItems='center'>
                            <PersonOutlineOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant='caption' color='text.secondary'>
                                By&nbsp;<Box component='span' sx={{ color: 'text.primary', fontWeight: 700 }}>{publishedBy}</Box>
                            </Typography>
                        </Stack>
                    )}
                    {audienceList.length > 0 && (
                        <Stack direction='row' spacing={0.75} alignItems='center' flexWrap='wrap'>
                            <GroupsOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            {audienceList.map((a) => (
                                <Chip
                                    key={a}
                                    label={a}
                                    size='small'
                                    sx={{
                                        backgroundColor: '#e0e7ff',
                                        color: '#4338ca',
                                        fontWeight: 600,
                                        height: 20,
                                        fontSize: 10,
                                    }}
                                />
                            ))}
                        </Stack>
                    )}
                    <Stack direction='row' spacing={0.75} alignItems='center'>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#4f46e5' }} />
                        <Typography variant='caption' color='text.secondary'>
                            <Box component='span' sx={{ color: 'text.primary', fontWeight: 700 }}>{changes.length}</Box>
                            &nbsp;{changes.length === 1 ? 'change' : 'changes'}
                        </Typography>
                    </Stack>
                </Stack>
            </Card>

            {/* ───── two-column body ───── */}
            <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
                {/* left: changes */}
                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
                        <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                            Changes
                        </Typography>
                        <Chip label={changes.length} size='small' sx={{ height: 18, fontSize: 10, backgroundColor: '#f3f4f6', color: 'text.secondary' }} />
                    </Stack>

                    <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', backgroundColor: '#fff' }}>
                        {changeTypes.length > 0 && (
                            <Tabs
                                value={tab}
                                onChange={(_, v) => setTab(v)}
                                variant='scrollable'
                                sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'grey.200',
                                    minHeight: 44,
                                    px: 1,
                                    '& .MuiTab-root': {
                                        minHeight: 44,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: 13,
                                    },
                                    '& .MuiTabs-indicator': {
                                        height: 3,
                                        borderRadius: 1.5,
                                    },
                                }}
                            >
                                {changeTypes.length > 1 && (
                                    <Tab
                                        label={
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                All
                                                <Box component='span' sx={{ backgroundColor: '#f3f4f6', color: 'text.secondary', borderRadius: 1, px: 0.75, fontSize: 10, fontWeight: 700 }}>
                                                    {changes.length}
                                                </Box>
                                            </span>
                                        }
                                    />
                                )}
                                {changeTypes.map(([t, count]) => {
                                    const c = typeStyle(t)
                                    return (
                                        <Tab
                                            key={t}
                                            label={
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: c.dot }} />
                                                    {titleCase(t)}
                                                    <Box component='span' sx={{ backgroundColor: c.bg, color: c.fg, borderRadius: 1, px: 0.75, fontSize: 10, fontWeight: 700 }}>
                                                        {count}
                                                    </Box>
                                                </span>
                                            }
                                        />
                                    )
                                })}
                            </Tabs>
                        )}

                        <Box sx={{ p: 3 }}>
                            {filteredChanges.length === 0 && (
                                <Typography variant='body2' color='text.secondary'>No changes listed.</Typography>
                            )}
                            {filteredChanges.map((change, idx) => {
                                const t = (change.type || change.changeType || 'update').toString()
                                const c = typeStyle(t)
                                return (
                                    <Box
                                        key={idx}
                                        sx={{
                                            position: 'relative',
                                            pl: 2.5,
                                            pb: idx === filteredChanges.length - 1 ? 0 : 3,
                                            mb: idx === filteredChanges.length - 1 ? 0 : 3,
                                            borderBottom: idx === filteredChanges.length - 1 ? 'none' : '1px solid',
                                            borderColor: 'grey.100',
                                            '&:before': {
                                                content: '""',
                                                position: 'absolute',
                                                left: 0,
                                                top: 4,
                                                bottom: idx === filteredChanges.length - 1 ? 4 : 28,
                                                width: 3,
                                                borderRadius: 1.5,
                                                backgroundColor: c.dot,
                                            },
                                        }}
                                    >
                                        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.25 }}>
                                            <Chip
                                                label={
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                        <span style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: c.dot }} />
                                                        {titleCase(t)}
                                                    </span>
                                                }
                                                size='small'
                                                sx={{
                                                    backgroundColor: c.bg,
                                                    color: c.fg,
                                                    fontWeight: 700,
                                                    fontSize: 11,
                                                    height: 22,
                                                    '& .MuiChip-label': { px: 1 },
                                                }}
                                            />
                                            {(change.title || change.heading) && (
                                                <Typography variant='subtitle2' fontWeight={700}>
                                                    {change.title || change.heading}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Box sx={{ color: 'text.primary' }}>
                                            {renderMarkdown(change.description || change.content || change.body || change.text || '')}
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Box>
                    </Card>
                </Box>

                {/* right: release info sidebar */}
                <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
                    <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', backgroundColor: '#fff' }}>
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                Release Info
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2 }}>
                            <Stack spacing={1.75}>
                                <InfoRow icon={<CalendarTodayOutlinedIcon sx={infoIconSx} />} label='Release Date' value={formatDate(releaseDate)} />
                                <InfoRow icon={<AccessTimeOutlinedIcon sx={infoIconSx} />} label='Published At' value={formatDate(publishedAt)} />
                                {publishedBy && <InfoRow icon={<PersonOutlineOutlinedIcon sx={infoIconSx} />} label='Published By' value={publishedBy} />}
                                {createdBy && <InfoRow icon={<PersonOutlineOutlinedIcon sx={infoIconSx} />} label='Created By' value={createdBy} />}
                                {modifiedBy && <InfoRow icon={<EditOutlinedIcon sx={infoIconSx} />} label='Modified By' value={modifiedBy} />}
                                <InfoRow icon={<UpdateOutlinedIcon sx={infoIconSx} />} label='Last Updated' value={formatDate(lastUpdated)} />
                            </Stack>
                        </Box>

                        {audienceList.length > 0 && (
                            <>
                                <Divider />
                                <Box sx={{ p: 2 }}>
                                    <Stack direction='row' spacing={0.75} alignItems='center' sx={{ mb: 1 }}>
                                        <GroupsOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                        <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                            Audience
                                        </Typography>
                                    </Stack>
                                    <Stack direction='row' spacing={0.75} flexWrap='wrap' sx={{ rowGap: 0.75 }}>
                                        {audienceList.map((a) => (
                                            <Chip
                                                key={a}
                                                label={a}
                                                size='small'
                                                sx={{
                                                    backgroundColor: '#e0e7ff',
                                                    color: '#4338ca',
                                                    fontWeight: 600,
                                                    fontSize: 11,
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </>
                        )}
                    </Card>
                </Box>
            </Box>
        </>
    )
}

const infoIconSx = { fontSize: 16, color: 'text.secondary' }

function InfoRow({ icon, label, value }) {
    return (
        <Stack direction='row' spacing={1.25} alignItems='flex-start'>
            <Box sx={{ mt: 0.25 }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='caption' color='text.secondary' sx={{
                    display: 'block',
                    textTransform: 'uppercase',
                    fontSize: 10,
                    letterSpacing: 0.6,
                    fontWeight: 700,
                    mb: 0.25,
                }}>
                    {label}
                </Typography>
                <Typography variant='body2' color='text.primary' fontWeight={600} sx={{ wordBreak: 'break-word', lineHeight: 1.4 }}>
                    {value}
                </Typography>
            </Box>
        </Stack>
    )
}
